import { useState, useEffect, lazy, Suspense, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { getCache, setCache, CACHE_CONFIG, invalidateCachePattern, broadcastCacheInvalidation } from "@/utils/cacheManager";
import { recordApiCall } from "@/utils/performanceMonitor";
import { getFinancialWebSocket } from "@/utils/financialWebSocket";

// Error Boundary Component to prevent blank screens
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Something went wrong</h2>
            <p className="text-slate-600 mb-6">An unexpected error occurred. Please try refreshing the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy load ALL pages for optimal performance
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LandingPageEditor = lazy(() => import("@/pages/LandingPageEditor"));
const CompanyProfilePage = lazy(() => import("@/pages/CompanyProfilePage"));

// Auth Pages
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage"));

// Customer Pages
const CustomerDashboard = lazy(() => import("@/pages/customer/CustomerDashboard"));
const NewOrderPage = lazy(() => import("@/pages/customer/NewOrderPage"));
const OrderDetailsPage = lazy(() => import("@/pages/customer/OrderDetailsPage"));
const CustomerProfilePage = lazy(() => import("@/pages/customer/CustomerProfilePage"));

// Admin Pages
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminPricingPage = lazy(() => import("@/pages/admin/AdminPricingPageNew"));
const AdminDesignGallery = lazy(() => import("@/pages/admin/AdminDesignGallery"));
const AdminMigrationPage = lazy(() => import("@/pages/admin/AdminMigrationPage"));
const DatabaseStatusPage = lazy(() => import("@/pages/admin/DatabaseStatusPage"));
const LinkStaticFilesPage = lazy(() => import("@/pages/admin/LinkStaticFilesPage"));
const UserManagementPage = lazy(() => import("@/pages/admin/UserManagementPage"));
const DeliveryDashboard = lazy(() => import("@/pages/admin/DeliveryDashboard"));
const AdminEditOrderPage = lazy(() => import("@/pages/admin/AdminEditOrderPage"));
const TemplatesPage = lazy(() => import("@/pages/admin/TemplatesPage"));
const TransferApprovalsPage = lazy(() => import("@/pages/admin/TransferApprovalsPage"));
const FinancialManagementPage = lazy(() => import("@/pages/admin/FinancialManagementPage"));
const CompletedOrdersPage = lazy(() => import("@/pages/admin/CompletedOrdersPage"));
const CustomersPage = lazy(() => import("@/pages/admin/CustomersPage"));
const QuotationsPage = lazy(() => import("@/pages/admin/QuotationsPage"));
const QuotationEditorPage = lazy(() => import("@/pages/admin/NewQuotationPage"));
const FinancialRecalculationPage = lazy(() => import("@/pages/admin/FinancialRecalculationPage"));
const ProductsPage = lazy(() => import("@/pages/admin/ProductsPage"));
const ProductEditorPage = lazy(() => import("@/pages/admin/ProductEditorPage"));
const ProductionStatisticsPage = lazy(() => import("@/pages/admin/ProductionStatisticsPage"));

// Ensure BACKEND_URL has protocol
const BACKEND_URL_RAW = process.env.REACT_APP_BACKEND_URL || '';
const BACKEND_URL = BACKEND_URL_RAW.startsWith('http') 
  ? BACKEND_URL_RAW 
  : BACKEND_URL_RAW 
    ? `https://${BACKEND_URL_RAW}` 
    : '';
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth
const api = axios.create({
  baseURL: API,
  timeout: 60000, // 60 second timeout for large orders
  maxContentLength: 50 * 1024 * 1024, // 50MB max
  maxBodyLength: 50 * 1024 * 1024, // 50MB max
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add request start time for performance monitoring
  config.requestStartTime = Date.now();
  
  // Enhanced API caching with intelligent cache control
  if (config.method === 'get') {
    // Auto-enable caching for safe endpoints unless explicitly disabled
    const shouldCache = config.useCache !== false && (
      config.useCache === true ||
      config.url.includes('/designs') ||
      config.url.includes('/materials') ||
      config.url.includes('/pricing') ||
      config.url.includes('/company-profile') ||
      config.url.includes('/templates') ||
      config.url.includes('/products') ||
      (config.url.includes('/orders') && !config.url.includes('/admin/orders')) ||
      config.url.includes('/customers')
    );
    
    if (shouldCache) {
      const cacheKey = `api_${config.url}_${JSON.stringify(config.params || {})}`;
      const cached = getCache(cacheKey);
      
      if (cached) {
        console.debug('[Cache] Serving from cache:', config.url);
        // Record cache hit
        recordApiCall(config.url, 0, true);
        // Return cached data
        config.adapter = () => {
          return Promise.resolve({
            data: cached,
            status: 200,
            statusText: 'OK (Cached)',
            headers: { 'x-cache': 'HIT' },
            config,
            request: {},
          });
        };
      }
    }
  }
  
  return config;
});

// Cache successful GET responses and handle cache invalidation
api.interceptors.response.use(
  (response) => {
    // Record API call performance
    if (response.config.requestStartTime) {
      const duration = Date.now() - response.config.requestStartTime;
      const fromCache = response.headers['x-cache'] === 'HIT';
      recordApiCall(response.config.url, duration, fromCache);
    }
    
    // Cache GET responses for appropriate endpoints
    if (response.config.method === 'get') {
      const shouldCache = response.config.useCache !== false && (
        response.config.useCache === true ||
        response.config.url.includes('/designs') ||
        response.config.url.includes('/materials') ||
        response.config.url.includes('/pricing') ||
        response.config.url.includes('/company-profile') ||
        response.config.url.includes('/templates') ||
        response.config.url.includes('/products') ||
        (response.config.url.includes('/orders') && !response.config.url.includes('/admin/orders')) ||
        response.config.url.includes('/customers')
      );
      
      if (shouldCache && !response.headers['x-cache']) { // Don't re-cache cached responses
        const cacheKey = `api_${response.config.url}_${JSON.stringify(response.config.params || {})}`;
        
        // Determine TTL based on endpoint type
        let ttl = CACHE_CONFIG.TTL.MEDIUM;
        if (response.config.url.includes('/pricing') || response.config.url.includes('/company-profile')) {
          ttl = CACHE_CONFIG.TTL.LONG;
        } else if (response.config.url.includes('/orders') || response.config.url.includes('/customers')) {
          ttl = CACHE_CONFIG.TTL.SHORT;
        }
        
        try {
          setCache(cacheKey, response.data, ttl);
          console.debug('[Cache] Cached response for:', response.config.url);
        } catch (error) {
          console.warn('[Cache] Failed to cache:', error);
        }
      }
    }
    
    // Handle cache invalidation for write operations
    if (['post', 'put', 'patch', 'delete'].includes(response.config.method.toLowerCase())) {
      const url = response.config.url;
      let invalidationPattern = null;
      
      // Determine what cache to invalidate based on the endpoint
      if (url.includes('/orders')) {
        invalidationPattern = 'api_.*orders';
        broadcastCacheInvalidation(invalidationPattern);
        // Also invalidate financial data when orders are modified
        const financialPattern = 'api_.*financials';
        broadcastCacheInvalidation(financialPattern);
        invalidateCachePattern(financialPattern);
        console.debug('[Cache] Invalidated financial cache due to order update');
        // Also invalidate quotations cache when orders are modified
        // This ensures quotations page shows updated data after roster edits
        const quotationPattern = 'api_.*quotations';
        broadcastCacheInvalidation(quotationPattern);
        invalidateCachePattern(quotationPattern);
        console.debug('[Cache] Invalidated quotations cache due to order update');
      } else if (url.includes('/customers')) {
        invalidationPattern = 'api_.*customers';
        broadcastCacheInvalidation(invalidationPattern);
      } else if (url.includes('/designs')) {
        invalidationPattern = 'api_.*designs';
        broadcastCacheInvalidation(invalidationPattern);
      } else if (url.includes('/materials')) {
        invalidationPattern = 'api_.*materials';
        broadcastCacheInvalidation(invalidationPattern);
      } else if (url.includes('/quotations')) {
        invalidationPattern = 'api_.*quotations';
        broadcastCacheInvalidation(invalidationPattern);
      } else if (url.includes('/products')) {
        invalidationPattern = 'api_.*products';
        broadcastCacheInvalidation(invalidationPattern);
      }
      
      if (invalidationPattern) {
        // Invalidate local cache
        invalidateCachePattern(invalidationPattern);
        console.debug('[Cache] Invalidated cache pattern:', invalidationPattern);
      }
    }
    
    return response;
  },
  (error) => {
    // Record failed API call performance
    if (error.config && error.config.requestStartTime) {
      const duration = Date.now() - error.config.requestStartTime;
      recordApiCall(error.config.url, duration, false);
    }
    return Promise.reject(error);
  }
);

export { api };

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Initialize Financial WebSocket when user logs in
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem("token");
      const ws = getFinancialWebSocket();
      
      // Connect to financial updates
      ws.connect(token);
      
      // Listen for financial updates
      ws.on('order.financial.updated', (event) => {
        console.debug('💰 Financial update received:', event);
        
        // Invalidate order caches to trigger refresh
        invalidateCachePattern('api_.*orders');
        broadcastCacheInvalidation('api_.*orders');
        
        // Show toast notification
        toast.info(`Order #${event.order_id.slice(0, 8)} financial data updated`, {
          description: `Total: ${event.snapshot?.total_due || 'N/A'} MVR`,
          duration: 3000
        });
      });
      
      // Listen for connection status
      ws.on('connection.established', () => {
        console.debug('✅ Connected to financial updates');
      });
      
      ws.on('connection.closed', () => {
        console.debug('⚠️  Disconnected from financial updates');
      });
      
      // Cleanup on logout
      return () => {
        // Keep connection alive, just stop listening
        // ws.disconnect();
      };
    }
  }, [user]);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await api.get("/auth/me");
        if (response.data && response.data.id) {
          setUser(response.data);
        } else {
          // Invalid user data, clear token
          console.warn("[Auth] Invalid user data received, clearing token");
          localStorage.removeItem("token");
          setUser(null);
        }
      } catch (error) {
        console.warn("[Auth] Token validation failed:", error.message);
        localStorage.removeItem("token");
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Suspense fallback={
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-slate-600">Loading...</p>
              </div>
            </div>
          }>
            <Routes>
            <Route path="/" element={<LandingPage user={user} logout={logout} />} />
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to={
                  (user.role === "admin" || user.role === "manager") ? "/admin" : 
                  user.role === "delivery" ? "/delivery" : 
                  "/dashboard"
                } />
              ) : (
                <LoginPage onLogin={login} />
              )
            }
          />
          <Route
            path="/register"
            element={user ? <Navigate to="/dashboard" /> : <RegisterPage onRegister={login} />}
          />
          <Route
            path="/forgot-password"
            element={user ? <Navigate to="/dashboard" /> : <ForgotPasswordPage />}
          />
          <Route
            path="/dashboard"
            element={
              user && (user.role === "customer" || user.role === "admin" || user.role === "manager") ? (
                <CustomerDashboard user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/new-order"
            element={
              user && (user.role === "customer" || user.role === "admin" || user.role === "manager") ? (
                <NewOrderPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/new-order/:orderId"
            element={
              user && (user.role === "customer" || user.role === "admin" || user.role === "manager") ? (
                <NewOrderPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/edit-order/:orderId"
            element={
              user ? (
                <NewOrderPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              user ? <OrderDetailsPage user={user} logout={logout} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/edit-order/:orderId"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <NewOrderPage user={user} logout={logout} editMode={true} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/edit-roster/:orderId"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <NewOrderPage user={user} logout={logout} editMode={true} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <AdminDashboard user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/pricing"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <AdminPricingPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/templates"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <TemplatesPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/transfer-approvals"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <TransferApprovalsPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/company-profile"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <CompanyProfilePage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/completed-orders"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <CompletedOrdersPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          {/* Alias route for completed orders */}
          <Route
            path="/admin/orders/completed"
            element={<Navigate to="/admin/completed-orders" replace />}
          />
          <Route
            path="/admin/customers"
            element={
              user && (user.role === "admin" || user.role === "manager" || user.role === "salesteam") ? (
                <CustomersPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/customers/:customerId"
            element={
              user && (user.role === "admin" || user.role === "manager" || user.role === "salesteam") ? (
                <CustomerProfilePage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/financial-management"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <FinancialManagementPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/admin/production-statistics"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <ProductionStatisticsPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/admin/designs"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <AdminDesignGallery user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/landing-page"
            element={
              user && user.role === "admin" ? (
                <LandingPageEditor user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/users"
            element={
              user && user.role === "admin" ? (
                <UserManagementPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/migration"
            element={
              user && user.role === "admin" ? (
                <AdminMigrationPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/database-status"
            element={
              user && user.role === "admin" ? (
                <DatabaseStatusPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/link-static-files"
            element={
              user && user.role === "admin" ? (
                <LinkStaticFilesPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/delivery"
            element={
              user && (user.role === "delivery" || user.role === "admin" || user.role === "manager") ? (
                <DeliveryDashboard user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          {/* Alias route for delivery dashboard */}
          <Route
            path="/admin/delivery"
            element={<Navigate to="/delivery" replace />}
          />
          <Route
            path="/admin/quotations"
            element={
              user && (user.role === "admin" || user.role === "manager" || user.role === "salesteam") ? (
                <QuotationsPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/quotations/:quotationId"
            element={
              user && (user.role === "admin" || user.role === "manager" || user.role === "salesteam") ? (
                <QuotationEditorPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/financial/recalculate"
            element={
              user && user.role === "admin" ? (
                <FinancialRecalculationPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/products"
            element={
              user && (user.role === "admin" || user.role === "manager") ? (
                <ProductsPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/products/:productId"
            element={
              user && user.role === "admin" ? (
                <ProductEditorPage user={user} logout={logout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          {/* Catch-all route - redirect unknown routes to home or login */}
          <Route
            path="*"
            element={user ? <Navigate to={user.role === "admin" || user.role === "manager" ? "/admin" : "/dashboard"} replace /> : <Navigate to="/login" replace />}
          />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;